import { useEffect, useState } from "react";
import { getProposal, proposalPdfUrl, respondProposal } from "../../crud/proposal.crud";

// Página pública da proposta — o que o cliente abre pelo link de aceite.
//
// Sem login: o token do link é a credencial, e ele só existe para proposta que um administrador
// aprovou e enviou. O cliente vê o resumo e o preço, baixa o PDF e responde. A resposta fica
// registrada com nome e data, e depois dela a página só mostra o que foi decidido.

const money = (value) => (value === null || value === undefined ? "—" : Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

const date = (value) => (value ? new Date(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }) : "—");

export default function ProposalPublicPage({ match }) {
    const token = match.params.token;

    const [proposal, setProposal] = useState(null);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState(null); // "ACCEPTED" | "REFUSED" enquanto o formulário está aberto
    const [name, setName] = useState("");
    const [reason, setReason] = useState("");
    const [sending, setSending] = useState(false);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        getProposal(token)
            .then((res) => setProposal(res.data))
            .catch((err) => setError(err?.response?.data?.errors?.[0] ?? "Proposta não encontrada ou não está mais disponível."));
    }, [token]);

    const submit = (event) => {
        event.preventDefault();

        if (name.trim().length < 3) {
            setFormError("Informe seu nome.");

            return;
        }

        setSending(true);
        setFormError(null);

        respondProposal(token, { decision: mode, name: name.trim(), reason: reason.trim() || null })
            .then((res) => {
                setProposal(res.data);
                setMode(null);
            })
            .catch((err) => setFormError(err?.response?.data?.errors?.[0] ?? "Não foi possível registrar a resposta. Tente de novo."))
            .finally(() => setSending(false));
    };

    if (error) {
        return (
            <div className="proposal-page">
                <div className="proposal-card proposal-card--center">
                    <i className="fa fa-exclamation-circle proposal-empty-icon" />
                    <h1>Proposta indisponível</h1>
                    <p>{error}</p>
                    <p className="proposal-muted">Se precisa de uma proposta atualizada, fale com quem enviou o link.</p>
                </div>
            </div>
        );
    }

    if (!proposal) {
        return (
            <div className="proposal-page">
                <div className="proposal-card proposal-card--center">
                    <i className="fas fa-circle-notch fa-spin proposal-empty-icon" />
                    <p>Carregando a proposta…</p>
                </div>
            </div>
        );
    }

    const decision = proposal.decision;
    const closed = Boolean(decision) || proposal.expired;

    return (
        <div className="proposal-page">
            <div className="proposal-card">
                <header className="proposal-header">
                    <div>
                        <span className="proposal-eyebrow">Proposta de orçamento</span>
                        <h1>{proposal.partName || proposal.code}</h1>
                        <span className="proposal-muted">
                            {proposal.code}
                            {proposal.company ? ` · ${proposal.company}` : ""}
                            {proposal.customer ? ` · para ${proposal.customer}` : ""}
                        </span>
                    </div>
                    <a className="btn btn-secondary btn-bold" href={proposalPdfUrl(token)} target="_blank" rel="noopener noreferrer">
                        <i className="fa fa-file-pdf margin-icon" />
                        Baixar PDF
                    </a>
                </header>

                <section className="proposal-total">
                    <div>
                        <span className="proposal-muted">Total para {proposal.quantity} peça(s)</span>
                        <strong>{money(proposal.totalPrice)}</strong>
                        <span className="proposal-muted">{money(proposal.unitPrice)} por peça · impostos inclusos</span>
                    </div>
                    <dl>
                        <div>
                            <dt>Prazo de entrega</dt>
                            <dd>{proposal.deliveryDays ? `${proposal.deliveryDays} dias após o aceite` : "A combinar"}</dd>
                        </div>
                        <div>
                            <dt>Válida até</dt>
                            <dd>{date(proposal.expiresAt)}</dd>
                        </div>
                    </dl>
                </section>

                {proposal.quantityBreaks?.length > 1 ? (
                    <section className="proposal-breaks">
                        <h2>Preço por quantidade</h2>
                        <table>
                            <thead>
                                <tr>
                                    <th>Quantidade</th>
                                    <th>Por peça</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proposal.quantityBreaks.map((tier) => (
                                    <tr key={tier.quantity} className={tier.requested ? "is-requested" : ""}>
                                        <td>
                                            {tier.quantity} peça(s){tier.requested ? " — sua quantidade" : ""}
                                        </td>
                                        <td>{money(tier.unitPrice)}</td>
                                        <td>{money(tier.totalPrice)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="proposal-muted">Outra quantidade? Responda à pessoa que enviou a proposta para receber uma nova.</p>
                    </section>
                ) : null}

                {decision ? (
                    <section className={`proposal-decision proposal-decision--${decision.value === "ACCEPTED" ? "accepted" : "refused"}`}>
                        <i className={`fa ${decision.value === "ACCEPTED" ? "fa-check-circle" : "fa-times-circle"}`} />
                        <div>
                            <strong>{decision.value === "ACCEPTED" ? "Proposta aceita" : "Proposta recusada"}</strong>
                            <span>
                                por {decision.name} em {date(decision.at)}
                                {decision.reason ? ` — ${decision.reason}` : ""}
                            </span>
                            {decision.value === "ACCEPTED" ? <span>Obrigado! O pedido entrou na fila de produção e entraremos em contato para os próximos passos.</span> : null}
                        </div>
                    </section>
                ) : proposal.expired ? (
                    <section className="proposal-decision proposal-decision--refused">
                        <i className="fa fa-clock" />
                        <div>
                            <strong>A validade desta proposta terminou</strong>
                            <span>Peça uma proposta atualizada a quem enviou o link.</span>
                        </div>
                    </section>
                ) : null}

                {!closed && !mode ? (
                    <section className="proposal-actions">
                        <button type="button" className="btn btn-primary btn-bold btn-lg" onClick={() => setMode("ACCEPTED")}>
                            <i className="fa fa-check margin-icon" />
                            Aceitar proposta
                        </button>
                        <button type="button" className="btn btn-secondary btn-bold btn-lg" onClick={() => setMode("REFUSED")}>
                            Recusar
                        </button>
                    </section>
                ) : null}

                {!closed && mode ? (
                    <form className="proposal-form" onSubmit={submit}>
                        <h2>{mode === "ACCEPTED" ? "Confirmar aceite" : "Recusar proposta"}</h2>
                        <label htmlFor="proposal-name">Seu nome *</label>
                        <input id="proposal-name" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} autoFocus />
                        <label htmlFor="proposal-reason">{mode === "ACCEPTED" ? "Observação (opcional)" : "Motivo (opcional, ajuda a melhorar a próxima proposta)"}</label>
                        <textarea id="proposal-reason" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} maxLength={2000} />
                        {formError ? <p className="proposal-error">{formError}</p> : null}
                        <div className="proposal-form__buttons">
                            <button type="button" className="btn btn-secondary btn-bold" disabled={sending} onClick={() => setMode(null)}>
                                Voltar
                            </button>
                            <button type="submit" className={`btn btn-bold ${mode === "ACCEPTED" ? "btn-primary" : "btn-danger"}`} disabled={sending}>
                                {sending ? "Enviando…" : mode === "ACCEPTED" ? "Aceitar proposta" : "Recusar proposta"}
                            </button>
                        </div>
                    </form>
                ) : null}

                <footer className="proposal-footer">Proposta gerada pelo Orçamentista Digital e revisada por um técnico antes do envio.</footer>
            </div>
        </div>
    );
}
